import isEqual from 'lodash/isEqual'
import isNull from 'lodash/isNull'
import omit from 'lodash/omit'

const initialState = {
  // baseURL used in every request
  baseURL: null,
  // Base params, present in every request
  baseParams: null,
  // Last used params
  params: null,
  // Tells if the current fetch is still going on
  loading: false,
  // Last time a fetch was attempted
  lastFetchedAt: null,
  // Data fetched. This is only populated as a result of a fetch operation
  data: null,
  // Error object. This is only populated as a result of a fetch operation
  error: null,
  // Special pagination data derived from fetched data, might be read and modified by external operations.
  // Pagination is a special substate, as it depends on the response from the endpoint
  // As such, the fetcher function is responsible for parsing and populating the pagination state
  pagination: null,
}

/**
 * Factory for creating general reducers that call APIs / fetch data along with their actions.
 * This will create a reducer that handles the 4 basic states:
 *   - Initial / Preloading data
 *   - Fetching data
 *   - Successful fetch
 *   - Unsuccessful fetch / Error
 *
 *  Filtering behaviour can be achieved by making use of params data.
 */
class FetcherFactory {
  /**
   * Constructor. Pretty self-explanatory.
   * @param name Used for building the reducer name
   * @param fetcher The fetcher function - used for actually calling external APIs
   * @param options Modify the Fetcher Factory according to many different options. These include:
   *  ** baseParams - Hardcoded query params that will be included on every fetcher request
   *  ** ignoreParamsCaching - By default, the fetcher DOES NOT re-fetch data if the params have not changed. This param ignores that
   *                         - Necessary to implement polling behaviour
   *  ** handleErrorAction - Custom state handler for the fetcher requests that result in an ERROR action
   *  ** handleSuccessAction - Custom state handler for the fetcher requests that result in a SUCCESS action
   */
  constructor(name, fetcher, options) {
    this.name = name
    this.fetcher = fetcher
    this.options = options

    this.actions = {
      // Sets the fetcher mode to "In Progress", dispatched when the FetchAction is MANUALLY called
      START: `FETCHER/${this.name}/START`,
      // Sets the data field, dispatched AUTOMATICALLY only on a successful fetch
      SUCCESS: `FETCHER/${this.name}/SUCCESS`,
      // Sets the error field, dispatched AUTOMATICALLY only on a failed fetch
      ERROR: `FETCHER/${this.name}/ERROR`,
      // Sets the pagination metadata, dispatched AUTOMATICALLY only on a successful fetch
      SET_PAGINATION: `FETCHER/${this.name}/SET_PAGINATION`,
      // Sets the current page, dispatched MANUALLY by the developer on page changes
      SET_PAGE: `FETCHER/${this.name}/SET_PAGE`,
      // Manually set the data, useful when you want to modify the data from outside the fetcher
      SET_DATA: `FETCHER/${this.name}/SET_DATA`,
    }
  }

  getReducer() {
    const enhancedInitialState = {
      ...initialState,
      baseParams: this.options?.baseParams,
    }

    return (state = enhancedInitialState, action) => {
      switch (action.type) {
        case this.actions.START:
          return {
            ...state,
            loading: true,
            params: action.params,
          }

        case this.actions.ERROR: {
          if (this.options?.handleErrorAction) {
            return this.options?.handleErrorAction(state, action.error)
          }

          return {
            ...state,
            loading: false,
            error: action.error,
            data: null,
            lastFetchedAt: Date.now(),
          }
        }

        case this.actions.SUCCESS: {
          if (this.options?.handleSuccessAction) {
            return this.options?.handleSuccessAction(state, action.data)
          }

          return {
            ...state,
            loading: false,
            error: null,
            data: action.data,
            lastFetchedAt: Date.now(),
          }
        }

        case this.actions.SET_PAGINATION:
          return {
            ...state,
            pagination: action.pagination,
          }

        case this.actions.SET_PAGE:
          return {
            ...state,
            pagination: {
              ...state.pagination,
              page: action.page,
            },
          }

        case this.actions.SET_DATA:
          return {
            ...state,
            data: action.data,
          }

        default:
          return state
      }
    }
  }

  getFetchAction(options = {}) {
    const {
      useMergedParams = false, // Uses the params from previous requests, useful for pagination
    } = options

    return (params = {}) => {
      return (dispatch, getState) => {
        let finalParams = params

        if (useMergedParams) {
          const state = getState()[this.name]
          let existingParams = state.params || {}
          existingParams = omit(existingParams, [ 'page' ])
          finalParams = {...existingParams, ...params}
        }

        return this.thunk.call(this, finalParams, null, dispatch, getState)
      }
    }
  }

  getSetDataAction() {
    return (data) => {
      return {
        type: this.actions.SET_DATA,
        data: data,
      }
    }
  }

  getSetPaginationAction() {
    return (pagination) => {
      return {
        type: this.actions.SET_PAGINATION,
        pagination: pagination,
      }
    }
  }

  getSetPageAction() {
    return (page) => {
      return {
        type: this.actions.SET_PAGE,
        page: page,
      }
    }
  }

  shouldResetPage (stateParams, newParams) {
    // If anything but the page changed, the page should be reset
    return !isEqual({...stateParams, page: null}, {...newParams, page: null})
  }

  /**
   * Action creator thunk
   */
  thunk(params, actionContext, dispatch, getState) {
    const state = getState()[this.name]

    const inputParams = isNull(params) ? {} : params
    const allParams = {
      ...this.options?.baseParams,
      page: state?.pagination?.page,
      ...inputParams,
    }

    // Do not refetch if the params are equal
    if (!this?.options?.ignoreParamsCaching && isEqual(state.params, allParams)) {
      return new Promise((resolve, reject) => {
        if (!state.error) {
          resolve({data: state.data, status: 200})
        } else {
          reject(state.error)
        }
      })
    }

    // Typically when filters or other params change from previous requests, the page should be reset
    if (this.shouldResetPage(state.params, allParams)) {
      allParams.page = 1

      dispatch({
        type: this.actions.SET_PAGINATION,
        pagination: {...state.pagination, page: 1},
      })
    }

    // Start fetching the data
    dispatch({
      type: this.actions.START,
      params: allParams,
    })

    return this.fetcher({params: allParams, state: state, dispatch: dispatch, factory: this})
      .then(({ data }) => {
        dispatch({
          type: this.actions.SUCCESS,
          data: data,
        })
      })
      .catch(( error ) => {
        dispatch({
          type: this.actions.ERROR,
          error: error,
        })

        throw new Error(error)
      })
  }
}

export default FetcherFactory
